import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useMemo } from 'react';
import { GeofenceTypeV2 } from '@casops/store/apis/geofencesApi';
import { ValidGeofenceNameRegex } from '@casops/store/apis/commonModels';

const WET_DURATION_MIN = 1;
const WET_DURATION_MAX = 500;
const GEOFENCE_NAME_MAX = 128;
const DESCRIPTION_MAX = 255;
const ASSET_NAME_MAX = 8;

export function useGeofenceFormSchema() {
  const { t } = useTranslation();

  const schema = useMemo(() => {
    const shapeSchema = z.object(
      {
        shape: z.string(),
        points: z.array(z.array(z.number())).min(1),
        radius: z.number().positive().nullable(),
      },
      {
        errorMap: () => ({ message: t('Validation.GeofenceShapeInstructions') }),
      },
    );

    const rulesSchema = z.object({
      areaIds: z.array(z.number().positive()),
      createEventOnEntry: z.boolean(),
      createEventOnExit: z.boolean(),
      createEventIfAtAndDidNotStop: z.boolean(),
      alarmLogicMuteAll: z.boolean(),
      alarmLogicRunAreaLogic: z.boolean(),
      createEventIfAtAndSpeeding: z.number().positive().nullable(),
      speedLimitLoaded: z.number().positive().nullable(),
      createEventIfAtForLongerThanSeconds: z.number().positive().nullable(),
      createEventIfAtForLessThanSeconds: z.number().positive().nullable(),
      interventionEnabled: z.boolean().nullable(),
      driveOutputs: z.array(z.number()),
      createEventOnInputs: z.array(z.number()),
    });

    return z
      .object({
        name: z
          .string()
          .trim()
          .min(1, { message: t('Validation.SpecifyGeofenceName') })
          .max(GEOFENCE_NAME_MAX, {
            message: t('Validation.GeofenceNameTooLong', { max: GEOFENCE_NAME_MAX }),
          })
          .regex(ValidGeofenceNameRegex, { message: t('Validation.InvalidCharacters') }),
        desc: z
          .string()
          .trim()
          .max(DESCRIPTION_MAX, {
            message: t('Validation.DescriptionTooLong', { max: DESCRIPTION_MAX }),
          }),
        fts: z
          .array(z.nativeEnum(GeofenceTypeV2, { message: t('Validation.SpecifyFenceType') }))
          .refine(
            v => !(v.includes(GeofenceTypeV2.WetRoad) && v.includes(GeofenceTypeV2.Intersection)),
            { message: t('Validation.CannotSpecifyWetAndIntersection') },
          ),
        wetDuration: z.number().nullable(),
        followMeName: z
          .string()
          .trim()
          .min(1, { message: t('Validation.SpecifyAssetName') })
          .max(ASSET_NAME_MAX, {
            message: t('Validation.AssetNameTooLong', { max: ASSET_NAME_MAX }),
          })
          .nullable(),
        stid: z.number().min(-1, { message: t('Validation.SpecifyStyleName') }),
        ags: z.array(z.number().positive()).min(1, { message: t('Validation.SpecifyAssetGroups') }),
        rules: rulesSchema,
        shape: shapeSchema,
      })
      .superRefine(({ fts, wetDuration }, ctx) => {
        if (fts.includes(GeofenceTypeV2.WetRoad)) {
          let message: string | undefined;

          if (wetDuration === null) {
            message = t('Validation.SpecifyWetDuration');
          } else if (wetDuration < WET_DURATION_MIN) {
            message = t('Validation.WetDurationMin', { min: WET_DURATION_MIN });
          } else if (wetDuration > WET_DURATION_MAX) {
            message = t('Validation.WetDurationMax', { max: WET_DURATION_MAX });
          }

          if (message) {
            ctx.addIssue({
              code: 'custom',
              message,
              path: ['wetDuration'],
            });
          }
        }
      });
  }, [t]);

  return schema;
}

export default useGeofenceFormSchema;
